/**
 * Buttons for the aircraft card: the suggested next step first, then the usual choices for the
 * aircraft's phase. Each is a typed command, so a click takes the same path as the command box,
 * and each is pre-checked against the pilot's rules: a button the pilot would refuse carries the
 * reason instead of transmitting. Pure; no DOM.
 */
import { bearingDeg, findRunwayEnd, toTrueDeg, type AircraftState, type AirportData } from '@atc/core';
import { checkInstruction, isAwaitingDeparture, isOnGroundPhase, isRemovedPhase, parseCommand, rejoinIndex, runwayUnable, type Aircraft, type Instruction, type InstructionPlan } from '@atc/sim';
import { hdg3, suggestAction, type HintRole } from './hints';
import { towerRunwayRisk } from '@atc/sim';

export interface CardAction {
  label: string;
  /** Typed command, callsign first. */
  command: string;
  /** The suggested next step (from the hints). */
  primary?: boolean;
  /** Why the pilot would refuse it right now. */
  disabledReason?: string;
}

/** Assumption: seven buttons cover the useful choices without turning the card into a form. */
const MAX_ACTIONS = 7;

const thousands = (ft: number) => `${(ft / 1000).toLocaleString('en-US')}k ft`;

function instructionLabel(i: Instruction, ac: Aircraft): string {
  switch (i.kind) {
    case 'heading': return `Heading ${hdg3(i.headingDeg)}`;
    case 'altitude': return `${isOnGroundPhase(ac.phase) ? 'Climb' : i.altitudeFt > ac.altitudeFt ? 'Climb' : 'Descend'} ${thousands(i.altitudeFt)}`;
    case 'direct': return `Direct ${i.fix}`;
    case 'hold': return `Hold ${i.fix}`;
    case 'speed': return `Speed ${i.speedKt}`;
    case 'land': return `Land ${i.runway}`;
    case 'approach': return `ILS ${i.runway}`;
    case 'takeoff': return 'Take off';
    case 'lineUp': return 'Line up';
    case 'abort': return ac.phase === 'takeoffRoll' ? 'Abort take-off' : 'Go around';
    case 'resumeNav': return 'Own navigation';
    case 'contact': return 'Contact departure';
  }
}

/** Instructions in a typed aircraft command, or null for anything else (RC, STANDBY, system commands). */
function instructionsOf(command: string): Instruction[] | null {
  const parsed = parseCommand(command);
  return parsed.ok && parsed.command.type === 'aircraft' ? parsed.command.instructions : null;
}

/**
 * The pilot's reason for refusing the command now, judged in order like one transmission and in
 * the same order the sim judges it (sim/src/index.ts `runTransmission`), so a key is greyed for
 * exactly the reason the pilot would give. `active` is the live traffic, read only, because
 * whether a runway is free depends on everyone else.
 */
export function unableReason(ac: Aircraft, command: string, airport: AirportData, active: readonly Aircraft[] = []): string | undefined {
  const plan: InstructionPlan = {};
  for (const i of instructionsOf(command) ?? []) {
    if (ac.control && i.kind === 'contact' && ac.control.offeredTo) continue;
    const reason = checkInstruction(ac, i, airport, plan) ?? (ac.control ? ac.control.coaching === false ? null : towerRunwayRisk(airport, active, ac, i) : runwayUnable(airport, active, ac, i));
    if (reason) return reason;
    if (i.kind === 'heading') plan.headingDeg = toTrueDeg(airport, i.headingDeg);
    else if (i.kind === 'direct') { const f = airport.fixes.get(i.fix); if (f) plan.headingDeg = bearingDeg(ac.lat, ac.lon, f.lat, f.lon); }
  }
  return undefined;
}

export function cardActions(ac: Aircraft, state: AircraftState, airport: AirportData, active: readonly Aircraft[] = [], role: HintRole = {}): CardAction[] {
  if (isRemovedPhase(ac.phase)) return [];
  const cs = ac.callsign, field = airport.elevationFt;
  const out: CardAction[] = [];
  const add = (command: string, primary = false, label?: string) => {
    if (out.some((a) => a.command === command)) return;
    const instructions = instructionsOf(command);
    const disabledReason = instructions ? unableReason(ac, command, airport, active) : undefined;
    out.push({ command, label: label ?? instructions?.map((i) => instructionLabel(i, ac)).join(' + ') ?? command, ...(primary ? { primary } : {}), ...(disabledReason ? { disabledReason } : {}) });
  };
  if (state.control) {
    if (state.control.owner !== 'tower' || state.control.frequency !== 'tower') return [];
    if (ac.phase === 'holdShort') { add(`${cs} W`, true); add(`${cs} T`); }
    else if (ac.phase === 'linedUp') add(`${cs} T`, true);
    else if (ac.phase === 'takeoffRoll') add(`${cs} A`, false, 'Stop immediately');
    else if (!ac.onGround && ac.approach?.established) {
      if (!ac.approach.cleared) add(`${cs} L ${ac.approach.runway.name}`, true);
      add(`${cs} A`, false, 'Go around');
    }
    if (state.control.offeredTo && state.control.offeredTo !== 'tower') add(`${cs} CT`, true, `Contact ${state.control.offeredTo}`);
    if (ac.pilot.request) add(`${cs} STANDBY`, false, 'Stand by');
    return out;
  }
  const request = ac.pilot.request && !ac.pilot.request.unvoiced ? ac.pilot.request.kind : undefined;

  const hint = suggestAction(state, airport, role);
  if (hint.command && hint.priority > 0) add(hint.command, true, / CTW$/.test(hint.command) ? 'Contact tower' : undefined);
  if (request === 'contact') add(`${cs} RC`, false, ac.kind === 'arrival' ? 'Radar contact' : 'Roger');

  if (isAwaitingDeparture(ac.phase)) {
    if (ac.assigned.altitudeFt === undefined) {
      add(`${cs} C ${Math.max(1, Math.round(airport.initialAltitudeFt / 1000))} T`);
      add(`${cs} T`);
    }
    else add(`${cs} T`);
    if (ac.phase === 'holdShort') add(`${cs} W`);
  } else if (ac.phase === 'takeoffRoll') {
    add(`${cs} A`);
  } else if (!isOnGroundPhase(ac.phase) && ac.kind === 'departure') {
    const handoff = Math.ceil((field + 4000) / 1000);
    if ((ac.assigned.altitudeFt ?? 0) < handoff * 1000) add(`${cs} C ${handoff}`);
    if (ac.requested && airport.fixes.has(ac.requested) && ac.assigned.fix !== ac.requested) add(`${cs} C ${ac.requested}`);
    add(`${cs} CT`);
  } else if (!isOnGroundPhase(ac.phase)) {
    const rw = ac.approach?.runway ?? findRunwayEnd(airport, airport.arrivalRunway);
    // Same level the hints descend to: the landing-clearance limit, in whole thousands.
    const low = Math.floor((field + 3000) / 1000);
    if (ac.altitudeFt > low * 1000 + 200 && ac.assigned.altitudeFt !== low * 1000) add(`${cs} C ${low}`);
    if (rw && !ac.approach) add(`${cs} I ${rw.name}`);
    // In an approach position the landing clearance is tower's: an established arrival is transferred instead.
    if (role.approachRole) { if (ac.approach?.established) add(`${cs} CTW`, false, 'Contact tower'); }
    else if (rw && !ac.approach?.cleared) add(`${cs} L ${rw.name}`);
    // Once tower has cleared it to land in an approach position, the go-around is tower's call too.
    if (ac.approach && !(role.approachRole && ac.approach.cleared)) add(`${cs} A`);
    else if (!ac.approach) {
      const leg = ac.flightPlan[rejoinIndex(ac, airport)];
      if (leg?.kind === 'fix' && airport.fixes.has(leg.fix) && ac.nav.mode !== 'hold') add(`${cs} H ${leg.fix}`);
      if (ac.nav.mode !== 'route' && ac.flightPlan.some((l) => l.kind === 'fix')) add(`${cs} RN`);
    }
  }
  if (request && request !== 'contact') add(`${cs} STANDBY`, false, 'Stand by');
  return out.slice(0, MAX_ACTIONS);
}
